/**
 * Converts a given input to a formatted string with commas as thousand separators.
 * Optionally converts the input from Rial to Toman and appends a currency symbol.
 * @param input - The input value to be converted and formatted.
 * @param options - Additional options for conversion and formatting.
 * @param options.convertToToman - Flag indicating whether to convert Rial to Toman. Default is true.
 * @param options.currencySymbol - The currency symbol to append after the formatted number. Default is an empty string.
 * @returns The formatted string.
 */
export default function formatCurrency(
    input: string | number,
    options: { convertToToman?: boolean; currencySymbol?: string } = {}
): string {
    const { convertToToman = true, currencySymbol = '' } = options;

    let value: string | number = input;

    // Convert Rial to Toman if required
    if (convertToToman) {
        value = convertRialToToman(input);
    }

    // Convert the value to a string and format with commas
    const formattedValue = formatWithCommas(value.toString());

    // Append the currency symbol if provided
    return formattedValue + (currencySymbol ? ` ${currencySymbol}` : '');
}

/**
 * Converts a given input from Rial to Toman.
 * @param input - The input value in Rial.
 * @returns The converted value in Toman.
 */
function convertRialToToman(input: string | number): string | number {
    if (typeof input === "number") {
        return input / 10;
    } else {
        return input.slice(0, -1);
    }
}

/**
 * Formats a given string with commas as thousand separators.
 * @param value - The string value to be formatted.
 * @returns The formatted string.
 */
function formatWithCommas(value: string): string {
    const parts = value.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
}
